import { useEffect, useRef, useState } from 'react'

/**
 * Tracks whether a ref'd element has ever scrolled into (or near) the viewport, latching to
 * `true` permanently the first time it does - scrolling back out never resets it.
 *
 * @param rootMargin - CSS-margin-style string expanding the viewport's intersection bounds, so
 * `hasBeenVisible` flips slightly before the element is actually on screen (avoids a visible
 * loading flash right as the user scrolls to it). Defaults to `'200px'` on every side.
 * @returns `ref` - attach to the wrapper element to observe.
 * @returns `hasBeenVisible` - `false` until the element first intersects, then `true` forever.
 */
const useHasBeenVisible = <T extends Element = HTMLDivElement>(rootMargin = '200px') => {
    const ref = useRef<T>(null)
    const [hasBeenVisible, setHasBeenVisible] = useState(false)

    useEffect(() => {
        if (hasBeenVisible || !ref.current) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setHasBeenVisible(true)
            },
            { rootMargin }
        )

        observer.observe(ref.current)

        return () => observer.disconnect()
    }, [hasBeenVisible, rootMargin])

    return { ref, hasBeenVisible } as const
}

export default useHasBeenVisible
